import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as DocumentPicker from "expo-document-picker"
import * as ImagePicker from "expo-image-picker"

// Relative imports (not the `@common`/`@data` aliases) — those have no tsconfig `paths`, so only relative
// paths resolve under `tsc`. These modules are JS (typed as `any` via `allowJs`); this hook is the
// app-coupled boundary that owns them so the DS never has to.
import { useCamera } from "../../../../common/hooks/useCamera"
import { documentPicker } from "../../../../common/components/pdf/documentPicker"
import { formIds } from "../../../../data/constants/formConstants"
import { validation } from "../../../../data/utility/validation"
import { clearLastPhoto, clearPhotos } from "../../../../data/redux/actions/camera"

type MediaImage = {
    uri: string
    width?: number
    height?: number
    mimeType?: string
    name?: string
    fileSize?: number
}

/** Le trajet du scanner (`useCamera`), pour qui veut le suivre. */
export type CameraScanEvents = {
    opened?: (info: { source: "scanner" | "camera_screen" }) => void
    returned?: (info: { pages: number }) => void
    cancelled?: () => void
    failed?: (error: unknown) => void
    unreturned?: (info: { sinceMs: number; leftApp: boolean; backForMs: number | null }) => void
}

export type UseMediaPickerAdapterConfig = {
    // controlled value (owned by the caller/form)
    images?: MediaImage[]
    setImages?: (next: MediaImage[]) => void
    pdf?: string | null
    setPdf?: (next: string | null) => void
    url?: string
    setUrl?: (next: string) => void
    // config
    formId?: string
    multiple?: boolean
    /**
     * Le nombre de pièces que la galerie laisse choisir d'un coup (avec `multiple`), quand l'appelant a
     * un plafond : le sélecteur l'impose, au lieu de garder les premières en silence. Sans valeur, pas
     * de plafond.
     */
    selectionLimit?: number
    buttonLabel?: string
    addMoreButtonLabel?: string
    showAddMoreButton?: boolean
    enableExternalLink?: boolean
    enableCamera?: boolean
    enableGallery?: boolean
    enableDocument?: boolean
    /** Web drag-and-drop + Ctrl+V. Default true; no-op on native. */
    enableDragAndDrop?: boolean
    videoCourseMode?: boolean
    /**
     * Les vidéos aussi (défaut `false`) : la galerie propose images et vidéos, le choix de fichier
     * images et vidéos (plus de PDF), et chaque pièce garde son type, son nom et son poids.
     */
    enableVideo?: boolean
    /**
     * Le scanner GeniusScan en natif (défaut `true`). `false` : l'appareil photo de l'app, une photo
     * dans `camera.lastPhoto` — le « Où j'en suis » de Sam, comme l'ancien `useSamWorkCheck`.
     */
    geniusScan?: boolean
    /** Le trajet du scanner, de son ouverture à son retour (la copie d'une activité le trace). */
    scanEvents?: CameraScanEvents
    onMenuStateChange?: (open: boolean) => void
}

/**
 * useMediaPickerAdapter — the screen-adapter hook for the DS `<MediaPicker>` organism.
 *
 * Turns the app's effectful world (redux camera state, the gallery/document/camera pickers, URL
 * validation) into the injected props the *pure* DS organism needs. A page/screen owns the media
 * state, calls this hook, and spreads the result straight onto `<MediaPicker>` — there is no
 * container component. Image / PDF / link viewing is owned inside `<MediaPicker>` via
 * `<MediaDisplay>` (legacy `MaterialDisplayModal` parity — no `Linking` escape hatch).
 *
 * Placement: `ports/primary/ui/hooks` is an **app-coupled** layer (redux / expo / app JS), the deliberate
 * opposite of the pure `ports/primary/ui/types` + `ports/primary/ui/formatters` the DS may import. The DS must NEVER import
 * from here — data flows one way: this hook builds props, the screen passes them down. (Excluded from
 * `ds:tsc` so the DS typecheck stays pure.)
 */
export const useMediaPickerAdapter = (config: UseMediaPickerAdapterConfig = {}) => {
    const {
        images = [],
        setImages,
        pdf = null,
        setPdf,
        url = "",
        setUrl,
        formId = formIds.NEW_MATERIAL,
        multiple = false,
        selectionLimit,
        buttonLabel = "Ajouter",
        addMoreButtonLabel = "Ajouter plus",
        showAddMoreButton = true,
        enableExternalLink = true,
        enableCamera = true,
        enableGallery = true,
        enableDocument = true,
        enableDragAndDrop = true,
        videoCourseMode = false,
        enableVideo = false,
        geniusScan = true,
        scanEvents,
        onMenuStateChange,
    } = config

    const dispatch = useDispatch()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastPhoto = useSelector((state: any) => state.camera?.lastPhoto)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastPhotos = useSelector((state: any) => state.camera?.lastPhotos)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastPhotoFormId = useSelector((state: any) => state.camera?.formId)

    const [launchCamera] = useCamera(formId, geniusScan, false, scanEvents)

    // Gallery adapter — the organism does the single-vs-multiple merge.
    const pickImages = async (): Promise<MediaImage[]> => {
        const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: enableVideo ? ["images", "videos"] : ["images"],
            quality: 1,
            allowsMultipleSelection: multiple,
            // 0 vaut « sans limite » pour expo-image-picker : un plafond se pose à 1 au moins.
            ...(multiple && selectionLimit != null ? { selectionLimit: Math.max(1, selectionLimit) } : {}),
        })
        if (result.canceled || !result.assets?.length) return []
        return result.assets.map((asset) => ({
            uri: asset.uri,
            width: asset.width,
            height: asset.height,
            // Avec les vidéos, de quoi juger la pièce avant de l'envoyer ; sans, l'image d'avant.
            ...(enableVideo
                ? {
                      mimeType: asset.mimeType ?? (asset.type === "video" ? "video/mp4" : "image/jpeg"),
                      name: asset.fileName ?? undefined,
                      fileSize: asset.fileSize,
                  }
                : {}),
        }))
    }

    // Document adapter — pdf or image; the organism routes on the type the file carries. Avec les
    // vidéos : images et vidéos, plus de PDF.
    const pickDocument = async (): Promise<{
        uri: string
        name?: string
        mimeType?: string
        fileSize?: number
    } | null> => {
        if (!enableVideo) return documentPicker()
        const result = await DocumentPicker.getDocumentAsync({ type: ["image/*", "video/*"] })
        const asset = result.canceled ? null : result.assets?.[0]
        if (!asset?.uri) return null
        return { uri: asset.uri, name: asset.name, mimeType: asset.mimeType, fileSize: asset.size }
    }

    // Pure URL validation (regex).
    const validateUrl = (value: string): boolean => {
        const check = validation.checkUrl()
        return check?.validate ? !!check.validate(value) : true
    }

    // Camera round-trip: freshly-arrived redux photos for THIS formId.
    const incomingCameraImages = useMemo<MediaImage[]>(() => {
        if (lastPhotoFormId !== formId) return []
        if (lastPhotos?.length) return lastPhotos
        if (lastPhoto) return [lastPhoto]
        return []
    }, [lastPhotoFormId, formId, lastPhotos, lastPhoto])

    const onCameraImagesConsumed = () => {
        dispatch(clearLastPhoto())
        dispatch(clearPhotos())
    }

    return {
        images,
        pdf,
        url,
        onImagesChange: setImages,
        onPdfChange: setPdf,
        onUrlChange: (next: string) => setUrl?.(next),
        pickImages,
        pickDocument,
        launchCamera,
        validateUrl,
        incomingCameraImages,
        onCameraImagesConsumed,
        multiple,
        buttonLabel,
        addMoreButtonLabel,
        showAddMoreButton,
        enableExternalLink,
        enableCamera,
        enableGallery,
        enableDocument,
        enableDragAndDrop,
        videoCourseMode,
        enableVideo,
        onMenuStateChange,
    }
}
